import React, { useMemo, useState, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { BottomNavigator } from "@ann/web-components";
import { InviteUsersContainer, InviteDescription, BottomNavigatorWrapper } from "./InviteUsers.styled";
import { UserDetails, RolesAndGroup, Review } from "./pages";
import { Header } from "./components";
import { enrollment } from "../../services";

const InviteUsers = ({ breadcrumbHelpers, inviteError, setInviteError, setInvitedUsersCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [emails, setEmails] = useState([]);
  const [assignRolesSearchValue, setAssignRolesSearchValue] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [rolesData, setRolesData] = useState([]); // Store complete role data
  const [assignGroupsSearchValue, setAssignGroupsSearchValue] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupsData, setGroupsData] = useState([]); // Store complete group data
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState(null);

  useEffect(() => {
    breadcrumbHelpers?.setPageBreadcrumb?.("Users", "Invite users", {
      pageHref: "/v2/user-management",
      subHref: "/v2/user-management/invite-user",
    });
  }, [breadcrumbHelpers]);

  const emailsCount = emails.length;

  const isFirstStep = useMemo(() => {
    const path = location.pathname;
    return path === "/v2/user-management/invite-user" || path.endsWith("invite-user/");
  }, [location.pathname]);

  const isRolesAndGroupsStep = useMemo(() => {
    const path = location.pathname;
    return path.includes("roles-and-groups");
  }, [location.pathname]);

  const disabledNext = useMemo(() => {
    if (isFirstStep) {
      return !emails.length || !selected.length;
    }
    // if (isRolesAndGroupsStep) {
    //   return !selectedRoles.length && !selectedGroups.length;
    // }
    return false;
  }, [isFirstStep, isRolesAndGroupsStep, emails.length, selected.length, selectedRoles.length, selectedGroups.length]);

  const navigationConfig = useMemo(() => {
    const path = location.pathname;

    if (path === "/v2/user-management/invite-user" || path.endsWith("invite-user/")) {
      return {
        backLabel: "User Management",
        nextLabel: "Roles and groups",
        nextIcon: "arrowRight",
        onBack: () => navigate("/v2/user-management"),
        onNext: () => navigate("/v2/user-management/invite-user/roles-and-groups"),
      };
    } else if (path.includes("roles-and-groups")) {
      return {
        backLabel: "Invite Users",
        nextLabel: "Review",
        nextIcon: "arrowRight",
        onBack: () => navigate("/v2/user-management/invite-user"),
        onNext: () => navigate("/v2/user-management/invite-user/review"),
      };
    } else if (path.includes("review")) {
      return {
        backLabel: "Roles and groups",
        nextLabel: "Send invites",
        nextIcon: "email",
        onBack: () => navigate("/v2/user-management/invite-user/roles-and-groups"),
        onNext: async () => {
          setIsEnrolling(true);
          setEnrollmentError(null);
          
          try {
            // Map selected user type to uppercase values expected by the API
            // API expects: ['ADMIN', 'SUPERADMIN', 'USER', 'OWNER']
            const userTypeMap = {
              user: "USER",
              admin: "ADMIN",
              superadmin: "SUPERADMIN",
            };
            const userType = userTypeMap[selected] || "USER";
            
            await enrollment({
              emails: emails,
              user_type: userType,
              roles: selectedRoles,
              groups: selectedGroups.filter(groupId => groupId != null),
            });
            
            // Success - navigate to finish page
            setInvitedUsersCount(emailsCount);
            navigate("/v2/user-management/finish");
          } catch (error) {
            console.error("Error enrolling users:", error);
            setEnrollmentError(error.message || "Failed to send invites. Please try again.");
          } finally {
            setIsEnrolling(false);
          }
        },
      };
    }

    return {
      backLabel: "Back",
      nextLabel: "Next",
      nextIcon: "arrowRight",
      onBack: () => navigate("/v2/user-management"),
      onNext: () => { },
    };
  }, [location.pathname, navigate, emailsCount, setInvitedUsersCount, emails, selected, selectedRoles, selectedGroups]);

  return (

    <InviteUsersContainer>

      <Header title="Invite User" description="Send invitations and assign permissions to new users in your workspace. Each invited user will receive an activation email and appear in your Users list." />
      <Routes>
        <Route
          index
          element={
            <UserDetails
              selected={selected}
              setSelected={setSelected}
              emails={emails}
              setEmails={setEmails}
              inviteError={inviteError}
              setInviteError={setInviteError}
            />
          }
        />
        <Route
          path="roles-and-groups"
          element={
            <RolesAndGroup
              emails={emails}
              selected={selected}
              assignRolesSearchValue={assignRolesSearchValue}
              setAssignRolesSearchValue={setAssignRolesSearchValue}
              selectedRoles={selectedRoles}
              setSelectedRoles={setSelectedRoles}
              rolesData={rolesData}
              setRolesData={setRolesData}
              assignGroupsSearchValue={assignGroupsSearchValue}
              setAssignGroupsSearchValue={setAssignGroupsSearchValue}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedGroups}
              groupsData={groupsData}
              setGroupsData={setGroupsData}
            />
          }
        />
        <Route
          path="review"
          element={
            <Review
              emails={emails}
              selected={selected}
              assignRolesSearchValue={assignRolesSearchValue}
              setAssignRolesSearchValue={setAssignRolesSearchValue}
              selectedRoles={selectedRoles}
              setSelectedRoles={setSelectedRoles}
              rolesData={rolesData}
              setRolesData={setRolesData}
              assignGroupsSearchValue={assignGroupsSearchValue}
              setAssignGroupsSearchValue={setAssignGroupsSearchValue}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedGroups}
              groupsData={groupsData}
              setGroupsData={setGroupsData}
              enrollmentError={enrollmentError}
              isEnrolling={isEnrolling}
            />
          }
        />
      </Routes>
      <BottomNavigatorWrapper>
        <BottomNavigator
          backIcon="arrowLeft"
          backLabel={navigationConfig.backLabel}
          nextIcon={navigationConfig.nextIcon}
          nextLabel={navigationConfig.nextLabel}
          onBack={navigationConfig.onBack}
          onNext={navigationConfig.onNext}
          disabledNext={disabledNext || isEnrolling}
        />
      </BottomNavigatorWrapper>
    </InviteUsersContainer>
  );
};

export default InviteUsers;

